import { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MatchResult, Lineup } from '@stanwars/sim';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { BattleScene } from '../battle/BattleScene';
import { useBattleStore } from '../store/battle';
import { usePlayerStore } from '../store/player';

type Phase = 'battle' | 'reward';

function toLineup(value: unknown): Lineup | null {
  if (value && typeof value === 'object' && 'slots' in (value as object)) {
    return value as Lineup;
  }
  return null;
}

// Role counter triangle: rapper > vocalist > dancer > rapper
const BEATS: Partial<Record<string, string>> = {
  rapper: 'vocalist',
  vocalist: 'dancer',
  dancer: 'rapper',
};

interface BattleAnalysis {
  totalDmgByA: number;
  totalDmgByB: number;
  koSufferedA: number;
  koSufferedB: number;
  skillsFiredA: number;
  skillsFiredB: number;
  roleAdvantageA: number;
  roleDisadvantageA: number;
}

function analyzeBattle(result: MatchResult, lineupA: Lineup, lineupB: Lineup): BattleAnalysis {
  const events = result.events;
  let totalDmgByA = 0, totalDmgByB = 0;
  let skillsFiredA = 0, skillsFiredB = 0;

  for (const ev of events) {
    if (ev.kind === 'damage') {
      // side = who received the damage
      if (ev.side === 'B') totalDmgByA += ev.amount;
      else totalDmgByB += ev.amount;
    } else if (ev.kind === 'skill_fire') {
      if (ev.side === 'A') skillsFiredA++;
      else skillsFiredB++;
    }
  }

  const koSufferedA = result.finalHpA.filter(hp => hp <= 0).length;
  const koSufferedB = result.finalHpB.filter(hp => hp <= 0).length;

  const bRoles = lineupB.slots.map(s => s.member.role as string);
  let roleAdvantageA = 0, roleDisadvantageA = 0;
  for (const slotA of lineupA.slots) {
    const roleA = slotA.member.role as string;
    const beats = BEATS[roleA];
    const beatenBy = Object.keys(BEATS).find(r => BEATS[r] === roleA);
    if (beats && bRoles.includes(beats)) roleAdvantageA++;
    if (beatenBy && bRoles.includes(beatenBy)) roleDisadvantageA++;
  }

  return { totalDmgByA, totalDmgByB, koSufferedA, koSufferedB, skillsFiredA, skillsFiredB, roleAdvantageA, roleDisadvantageA };
}

function StatRow({ label, valA, valB }: { label: string; valA: number | string; valB: number | string }) {
  return (
    <div className="flex justify-between items-center py-1 border-b border-ink-700 last:border-0">
      <PixelText size="sm" color="white" className="opacity-60">{label}</PixelText>
      <div className="flex gap-4">
        <PixelText size="sm" color="cyan">{valA}</PixelText>
        <PixelText size="sm" color="yellow">{valB}</PixelText>
      </div>
    </div>
  );
}

function BattleSummaryPanel({ result, lineupA, lineupB }: { result: MatchResult; lineupA: Lineup; lineupB: Lineup }) {
  const { t } = useTranslation();
  const analysis = analyzeBattle(result, lineupA, lineupB);
  const lost = result.winner === 'B';

  return (
    <>
      {/* Stats */}
      <PixelPanel className="w-full max-w-sm">
        <div className="flex justify-between mb-2">
          <PixelText size="base" color="pink">{t('battle.summary_title')}</PixelText>
          <div className="flex gap-4">
            <PixelText size="sm" color="cyan">YOU</PixelText>
            <PixelText size="sm" color="yellow">ENEMY</PixelText>
          </div>
        </div>
        <StatRow label={t('battle.dmg_dealt')} valA={analysis.totalDmgByA} valB={analysis.totalDmgByB} />
        <StatRow label={t('battle.kos_suffered')} valA={analysis.koSufferedA} valB={analysis.koSufferedB} />
        <StatRow label={t('battle.skills_used')} valA={analysis.skillsFiredA} valB={analysis.skillsFiredB} />
      </PixelPanel>

      {/* Role analysis */}
      {(analysis.roleAdvantageA > 0 || analysis.roleDisadvantageA > 0) && (
        <PixelPanel className="w-full max-w-sm">
          {analysis.roleAdvantageA > 0 && (
            <PixelText size="sm" color="green" className="block">
              {t('battle.analysis_advantage', { count: analysis.roleAdvantageA })}
            </PixelText>
          )}
          {analysis.roleDisadvantageA > 0 && (
            <PixelText size="sm" color="yellow" className={`block ${analysis.roleAdvantageA > 0 ? 'mt-1' : ''}`}>
              {t('battle.analysis_disadvantage', { count: analysis.roleDisadvantageA })}
            </PixelText>
          )}
          <PixelText size="sm" color="white" className="block mt-2 opacity-60">
            {lost ? t('battle.analysis_tip_loss') : t('battle.analysis_tip_win')}
          </PixelText>
        </PixelPanel>
      )}

      {/* How to improve */}
      <PixelPanel className="w-full max-w-sm">
        <PixelText size="base" color="cyan" className="block mb-2">{t('battle.tips_title')}</PixelText>
        <div className="flex flex-col gap-2">
          {[
            t('battle.tip_roles'),
            t('battle.tip_formation'),
            t('battle.tip_synergy'),
            t('battle.tip_loadout'),
          ].map((tip, i) => (
            <div key={i} className="flex gap-2 items-start">
              <PixelText size="sm" color="pink" className="flex-shrink-0">▸</PixelText>
              <PixelText size="sm" color="white" className="opacity-80">{tip}</PixelText>
            </div>
          ))}
        </div>
      </PixelPanel>

      {/* Next steps */}
      <PixelPanel className="w-full max-w-sm">
        <PixelText size="base" color="yellow" className="block mb-2">{t('battle.next_steps')}</PixelText>
        <div className="flex flex-col gap-2">
          {lost && (
            <Link to="/roster">
              <PixelButton variant="ghost" className="w-full text-sm">{t('battle.next_roster')}</PixelButton>
            </Link>
          )}
          <Link to="/gacha">
            <PixelButton variant="ghost" className="w-full text-sm">{t('battle.next_gacha')}</PixelButton>
          </Link>
          <Link to="/campaign">
            <PixelButton variant="ghost" className="w-full text-sm">{t('battle.next_campaign')}</PixelButton>
          </Link>
          <Link to="/ladder">
            <PixelButton variant="ghost" className="w-full text-sm">{t('battle.next_ladder')}</PixelButton>
          </Link>
        </div>
      </PixelPanel>
    </>
  );
}

export function BattlePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = usePlayerStore(s => s.me);
  const {
    matchId, kind, result, lineupA, lineupB,
    ladderDelta, newTier, promoted, themeName, attemptsLeft,
    clear
  } = useBattleStore();

  const [phase, setPhase] = useState<Phase>('battle');

  if (!me) return <Navigate to="/login" replace />;

  if (!matchId || !result) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <PixelText color="yellow">{t('battle.no_battle')}</PixelText>
        <Link to="/"><PixelButton>{t('common.home')}</PixelButton></Link>
      </main>
    );
  }

  const matchResult = result as MatchResult;
  const lineupAObj = toLineup(lineupA);
  const lineupBObj = toLineup(lineupB);

  if (!lineupAObj || !lineupBObj) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <PixelText color="yellow">{t('battle.battle_incomplete')}</PixelText>
        <Link to="/"><PixelButton>{t('common.home')}</PixelButton></Link>
      </main>
    );
  }

  const handleContinue = () => {
    clear();
    const dest =
      kind === 'campaign' ? '/campaign'
      : kind === 'daily' ? '/daily'
      : kind === 'ladder' ? '/ladder'
      : '/friends';
    navigate(dest);
  };

  if (phase === 'battle') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-ink-950">
        <BattleScene
          result={matchResult}
          lineupA={lineupAObj}
          lineupB={lineupBObj}
          onComplete={() => setPhase('reward')}
        />
      </main>
    );
  }

  const winner = matchResult.winner;

  return (
    <main className="min-h-screen flex flex-col items-center gap-4 p-6 pb-12">
      <PixelText size="2xl" color={winner === 'A' ? 'pink' : winner === 'B' ? 'yellow' : 'cyan'}>
        {winner === 'A' ? t('battle.victory') : winner === 'B' ? t('battle.defeat') : t('battle.draw')}
      </PixelText>

      {kind === 'ladder' && ladderDelta !== null && (
        <PixelPanel className="w-full max-w-sm text-center">
          <PixelText size="base" color="cyan">{t('battle.ladder_results')}</PixelText>
          <PixelText size="lg" color={ladderDelta >= 0 ? 'green' : 'yellow'} className="block mt-2">
            {ladderDelta >= 0 ? '+' : ''}{ladderDelta} LP
          </PixelText>
          {newTier && <PixelText size="sm" color="cyan" className="block mt-1">{t('battle.tier', { tier: newTier })}</PixelText>}
          {promoted && <PixelText size="base" color="pink" className="block mt-1">{t('battle.promoted')}</PixelText>}
        </PixelPanel>
      )}

      {kind === 'daily' && themeName && (
        <PixelPanel className="w-full max-w-sm text-center">
          <PixelText size="sm" color="yellow">{t('battle.daily_title', { theme: themeName })}</PixelText>
          {attemptsLeft !== null && (
            <PixelText size="sm" color="cyan" className="block mt-1">
              {t('battle.attempts_remaining', { count: attemptsLeft })}
            </PixelText>
          )}
        </PixelPanel>
      )}

      {(kind === 'campaign' || kind === 'friend') && (
        <PixelPanel className="w-full max-w-sm text-center">
          <PixelText size="base" color="yellow">{t('battle.rewards')}</PixelText>
          <PixelText size="sm" color="green" className="block mt-2">
            {t('battle.stage_clear', { stage: winner === 'A' ? 3 : winner === 'draw' ? 1 : 0 })}
          </PixelText>
        </PixelPanel>
      )}

      <BattleSummaryPanel result={matchResult} lineupA={lineupAObj} lineupB={lineupBObj} />

      <PixelButton onClick={handleContinue} className="mt-2 w-full max-w-sm">
        {t('battle.continue')}
      </PixelButton>
    </main>
  );
}
